import { type ReactNode, useState } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { IconButton } from '../IconButton/IconButton';
import { useMotion } from '../motion';

/**
 * HIERARCHY INSIDE A SHEET IS A PUSH, NOT A POPOVER (Arif, 2026-07-30, on his own
 * phone: the Adjust sheet's Filter, Sort and Columns opened DESKTOP POPOVERS floating
 * over the sheet, one of them nearly full-screen).
 *
 * Apple's Human Interface Guidelines put it the other way round for both halves of this:
 * a sheet presents ONE task, and going deeper into that task is a PUSH inside the sheet -
 * the child arrives from the trailing edge, the parent leaves toward the leading edge,
 * and a back control returns. A menu floating over a sheet is a second modal layer over a
 * modal layer, which is the shape iOS never uses and the one our own DESIGN.md rules out
 * ("avoid layering glass on glass").
 *
 * WHAT THIS PRIMITIVE IS: a container for a sheet's ROOT content plus a flat set of
 * sub-pages, one visible at a time, with the slide and the back control. It is deliberately
 * ONE level deep: a phone sheet that needs a stack needs a screen instead.
 *
 * CONTROLLED, like `Dialog`. The caller owns which page is showing, because the same
 * caller owns the sheet's `open` and has to reset the page when the sheet closes - a sheet
 * that reopens three levels into where it was last is a sheet that lost its place.
 *
 * ONLY THE VISIBLE PANE IS MOUNTED (plus the outgoing one, for as long as it slides).
 * Entering a sub-page therefore MOUNTS it, which is the property the filter builder's
 * draft depends on: it seeds from the applied filter when it appears and drops what was
 * typed when it leaves, exactly as it does in the desktop popover. The consequence is the
 * other way round too and is documented rather than worked around: local state inside the
 * ROOT content does not survive a drill-in, so a root that owns state keeps it above this
 * component.
 */
export interface SheetPage<Id extends string = string> {
  /** Stable id; also the key the enter animation restarts on. */
  readonly id: Id;
  /** The heading beside the back control. */
  readonly title: string;
  readonly content: ReactNode;
}

export interface SheetNavigatorProps<Id extends string = string> {
  readonly pages: readonly SheetPage<Id>[];
  /** `null` = the root content. */
  readonly pageId: Id | null;
  readonly onPageChange: (pageId: Id | null) => void;
  /** The back control's accessible name. */
  readonly backLabel?: string | undefined;
  /** The root page. */
  readonly children: ReactNode;
}

/** A keyed pane keeps its own content alive until its actual exit settles (spec D5). */
function SheetPane({
  active,
  animate,
  pushing,
  onExited,
  children,
}: {
  readonly active: boolean;
  readonly animate: boolean;
  readonly pushing: boolean;
  readonly onExited: () => void;
  readonly children: ReactNode;
}) {
  const motion = useMotion(active, { onExited });
  if (!motion.mounted) return null;
  return (
    <div
      ref={motion.ref}
      data-state={motion.state}
      aria-hidden={active ? undefined : true}
      inert={!active}
      className={cx(
        active
          ? animate && (pushing ? 'sheet-page-push-in' : 'sheet-page-pop-in')
          : cx(
              'pointer-events-none absolute inset-0 sheet-page-leaving',
              pushing ? 'sheet-page-push-out' : 'sheet-page-pop-out',
            ),
      )}
    >
      {children}
    </div>
  );
}

export function SheetNavigator<Id extends string = string>({
  pages,
  pageId,
  onPageChange,
  backLabel = 'Back',
  children,
}: SheetNavigatorProps<Id>) {
  const active = pages.find((page) => page.id === pageId) ?? null;

  /**
   * WHAT IS ON ITS WAY OUT. `null` means nothing is leaving; `{ id: null }` means the ROOT
   * is leaving, which is why this is a wrapper object rather than a bare `Id | null`.
   */
  const [leaving, setLeaving] = useState<{ readonly id: Id | null } | null>(null);
  const [shown, setShown] = useState(pageId);
  // Update before commit so the keyed outgoing pane is never unmounted and copied.
  if (shown !== pageId) {
    setLeaving({ id: shown });
    setShown(pageId);
  }

  // One level deep, so the direction is decided by where we ARRIVED: at a page = push,
  // back at the root = pop. Nothing has to remember a stack to know which way to slide.
  const pushing = pageId !== null;
  const leavingContent =
    leaving === null
      ? null
      : leaving.id === null
        ? children
        : (pages.find((page) => page.id === leaving.id)?.content ?? null);

  return (
    <div>
      {active !== null && (
        /**
         * The header does NOT slide with the content. It is the one control that has to
         * stay hittable while the panes move, and a back button that is mid-flight is a
         * back button a thumb misses. iOS cross-fades its own back control for the same
         * reason it does not slide it out of reach.
         */
        <div className="mb-2 flex items-center gap-1">
          <IconButton
            icon="chevron-left"
            label={backLabel}
            className="-ml-2"
            onClick={() => onPageChange(null)}
          />
          <h3 className="min-w-0 truncate text-base font-semibold text-ink">{active.title}</h3>
        </div>
      )}

      <div className="relative overflow-hidden">
        <SheetPane
          key={active === null ? 'root:' : `page:${active.id}`}
          active
          animate={leaving !== null}
          pushing={pushing}
          onExited={() => {}}
        >
          {active === null ? children : active.content}
        </SheetPane>
        {leaving !== null && (
          <SheetPane
            key={leaving.id === null ? 'root:' : `page:${leaving.id}`}
            active={false}
            animate
            pushing={pushing}
            onExited={() => setLeaving((current) => (current === leaving ? null : current))}
          >
            {leavingContent}
          </SheetPane>
        )}
      </div>
    </div>
  );
}

export interface DrillInRowProps {
  readonly icon: IconName;
  readonly label: string;
  /**
   * What this setting is right now, read without opening the page. HIG's disclosure rows
   * carry their current value in the trailing position for exactly this reason: a row that
   * says only "Filter" costs a tap to answer "is anything filtered".
   */
  readonly value?: string | undefined;
  readonly onClick: () => void;
}

/**
 * A row that DRILLS IN. The whole row is the target (a chevron on its own is a 16px hit
 * area on a phone - the argument CollapsibleSection already makes for its header), it
 * claims the touch floor, and the trailing chevron is the convention that says a tap goes
 * deeper rather than toggling something in place.
 */
export function DrillInRow({ icon, label, value, onClick }: DrillInRowProps) {
  return (
    <button
      type="button"
      /**
       * THE NAME IS PUNCTUATED, and this repo has been here before: the accessible-name
       * algorithm joins child nodes with a SPACE, which is what turned "Adjust" plus "A
       * filter is active." into "Adjust . A filter is active." on the button that opens
       * this sheet. The same join reads this row as "Filter None". The label still OPENS
       * with the visible word, which is what voice control matches on (WCAG 2.5.3).
       */
      aria-label={value === undefined ? label : `${label}, ${value}`}
      onClick={onClick}
      className={cx(
        'pressable touch-target flex w-full items-center gap-3 rounded-control px-3 text-left',
        'hover:bg-surface-hover',
      )}
    >
      <Icon name={icon} className="shrink-0 text-ink-muted" />
      <span className="shrink-0 text-base text-ink">{label}</span>
      {value !== undefined && (
        <span className="ml-auto min-w-0 truncate text-sm text-ink-muted">{value}</span>
      )}
      <Icon
        name="chevron-right"
        size="1rem"
        className={cx('shrink-0 text-ink-subtle', value === undefined && 'ml-auto')}
      />
    </button>
  );
}
